import { TFunction } from "i18next"
import {
    CourseBookingBasePriceBlockingReason,
    GenerateScheduledPaymentsByCourseMutation,
} from "../../../generated/graphql"

/**
 * Späteinstiegs-Preis-Quote (Late-Entry): die Teilmenge der Preview-Antwort, die der
 * Checkout für Gating + Submit-Vertrag braucht. Fingerprint + Kalkulationsversion
 * stammen ausschließlich aus der zuletzt erfolgreich gerenderten Server-Preview und
 * wandern atomar mit den Preisdaten in den Submit — niemals über localStorage.
 */
export type QuoteInfo = {
    fingerprint?: string | null
    calculationVersion?: string | null
    isBookable?: boolean | null
    blockingReason?: CourseBookingBasePriceBlockingReason | null
    validUntil?: string | null
    isPartialBooking?: boolean | null
    eligibleLessonCount?: number | null
    totalLessonCount?: number | null
    originalGrossPrice?: number | null
    serviceGrossPrice?: number | null
    reduction?: number | null
}

/** Sentinel: die Preview-Anfrage ist fehlgeschlagen (Netzwerk-/GraphQL-Fehler). */
export const QUOTE_ERROR = "error" as const

/**
 * Vier Zustände, die BookingSummary hält:
 * - `undefined`: Preview nicht angebunden (kein Late-Entry-Flow / Stub) → kein Gate.
 * - `null`: neuer Preview-Request läuft → gesperrt ("wird berechnet").
 * - `QUOTE_ERROR`: letzter Preview-Request ist fehlgeschlagen → gesperrt + Retry.
 * - `QuoteInfo`: fertige Quote.
 */
export type QuoteState = QuoteInfo | null | undefined | typeof QUOTE_ERROR

type PreviewResult = NonNullable<GenerateScheduledPaymentsByCourseMutation["generateScheduledPaymentsByCourse"]>

/** Baut die Quote-Teilmenge aus der Preview-Antwort. */
export const buildQuoteInfo = (preview: PreviewResult): QuoteInfo => ({
    fingerprint: preview.fingerprint,
    calculationVersion: preview.calculationVersion,
    isBookable: preview.isBookable,
    blockingReason: preview.blockingReason,
    validUntil: preview.validUntil,
    isPartialBooking: preview.isPartialBooking,
    eligibleLessonCount: preview.eligibleLessonCount,
    totalLessonCount: preview.totalLessonCount,
    originalGrossPrice: preview.originalGrossPrice,
    serviceGrossPrice: preview.serviceGrossPrice,
    reduction: preview.reduction,
})

/**
 * Altes Backend ohne Late-Entry-Vertrag: liefert weder Fingerprint noch
 * Kalkulationsversion. Dann Legacy-Verhalten (Submit ohne die beiden Felder), damit
 * bereits geladene alte Bundles / ältere Backends nicht brechen.
 */
export const isLegacyQuote = (quote: QuoteInfo): boolean => quote.fingerprint == null && quote.calculationVersion == null

export type LateEntryGate = {
    /** Submit gesperrt? */
    blocked: boolean
    /** Sichtbarer Grund (endkundengerecht), wenn gesperrt. */
    reason?: string
    /** validUntil ist erreicht/überschritten → automatischer Preview-Refresh nötig. */
    expired?: boolean
    /** Preview fehlgeschlagen → dem Nutzer einen Wiederholen-Button anbieten. */
    retry?: boolean
}

/**
 * Zusätzliches Submit-Gating aus der Preview-Quote (rein additiv zum bestehenden
 * Gating). Drei Zustände:
 * - `undefined`: Preview nicht angebunden (kein Late-Entry-Flow / Stub) → kein Gate.
 * - `null`: neuer Preview-Request läuft (Invalidierung beim Request-Start) → gesperrt.
 * - Objekt: fertige Quote → isBookable/validUntil/Fingerprint prüfen.
 */
export const getLateEntryGate = (quote: QuoteState, nowMs: number, t: TFunction, rejectedFingerprint?: string | null): LateEntryGate => {
    if (quote === undefined) return { blocked: false }
    if (quote === QUOTE_ERROR) {
        return { blocked: true, retry: true, reason: t("lateEntry.priceCalculationFailed", "Die Preisberechnung ist fehlgeschlagen. Bitte erneut versuchen.") }
    }
    if (quote === null) {
        return { blocked: true, reason: t("lateEntry.priceCalculating", "Der Preis wird berechnet …") }
    }
    if (isLegacyQuote(quote)) return { blocked: false }

    if (quote.isBookable === false) {
        const reason =
            quote.blockingReason === CourseBookingBasePriceBlockingReason.NoEligibleLessons
                ? t("lateEntry.notBookableCourseFinished", "Dieser Kurs ist bereits abgeschlossen und kann nicht mehr gebucht werden.")
                : t("lateEntry.notBookableTooFewLessons", "Für diesen Kurs sind nicht mehr genügend Kurseinheiten verfügbar.")
        return { blocked: true, reason }
    }

    // Nach PRICE_QUOTE_CHANGED: der soeben abgelehnte Fingerprint darf nicht erneut
    // abgesendet werden — sperren, bis die Preview einen NEUEN Fingerprint liefert.
    if (rejectedFingerprint && quote.fingerprint && quote.fingerprint === rejectedFingerprint) {
        return { blocked: true, reason: t("lateEntry.priceCalculating", "Der Preis wird berechnet …") }
    }

    if (quote.validUntil) {
        const validUntilMs = Date.parse(quote.validUntil)
        if (!isNaN(validUntilMs) && validUntilMs <= nowMs) {
            return { blocked: true, expired: true, reason: t("lateEntry.priceExpiredRecalculating", "Der Preis ist abgelaufen und wird neu berechnet.") }
        }
    }

    if (!quote.fingerprint || !quote.calculationVersion) {
        return { blocked: true, reason: t("lateEntry.priceCalculating", "Der Preis wird berechnet …") }
    }

    return { blocked: false }
}
